import React, { useRef } from "react"
import PropTypes from "prop-types"
import useElementDimensions from "hooks/useElementDimensions"
import InvisibleButton from "components/buttons/InvisibleButton"
import theme from "styles/theme"

const AccordionItem = ({
  id,
  title,
  children,
  isOpen,
  onRequestOpen,
  onRequestClose,
  className,
}) => {
  const contentRef = useRef()
  const { height: contentHeight = 1000 } =
    useElementDimensions(contentRef, 1000) || {}

  return (
    <div
      css={[
        {
          backgroundColor: theme.white,
          borderBottom: `8px solid ${theme.lightBlue30}`,
          cursor: isOpen ? "initial" : "pointer",
          transitionProperty: "box-shadow, border-bottom",
          transitionDuration: "250ms",
          ":hover": activeCardStyles,
        },
        isOpen && activeCardStyles,
      ]}
      className={className}
    >
      <InvisibleButton
        css={{
          padding: "15px 20px",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          userSelect: "none",
          color: theme.textGray,
          width: "100%",
          [theme.tablet]: {
            padding: "16px 17px",
          },
          [theme.mobile]: {
            padding: 13,
            alignItems: "flex-start",
          },
        }}
        id={`acc-${id}-title`}
        onClick={isOpen ? onRequestClose : () => onRequestOpen(id)}
        aria-expanded={isOpen}
        aria-controls={`acc-${id}-content`}
      >
        <h4
          css={{
            ...theme.t4,
            [theme.mobile]: {
              fontSize: 14,
            },
          }}
        >
          {title}
        </h4>
        <ToggleIcon
          css={{
            transition: "transform 250ms",
            transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
            color: theme.midBlue,
            width: 19,
            marginLeft: 56,
            [theme.tablet]: {
              width: 16,
              marginLeft: 24,
            },
            [theme.mobile]: { width: 14 },
          }}
        />
      </InvisibleButton>

      <div
        id={`acc-${id}-content`}
        role="region"
        aria-labelledby={`acc-${id}-title`}
        css={{
          overflow: "hidden",
          maxHeight: isOpen ? contentHeight : 0,
          transition: "max-height 250ms",
          backgroundColor: theme.nearWhite,
          visibility: isOpen ? "visible" : "hidden",
        }}
      >
        <div
          ref={contentRef}
          css={{
            transform: `translateY(${isOpen ? 0 : -20}px)`,
            transition: "transform 250ms, opacity 100ms",
            opacity: isOpen ? 1 : 0,
            padding: 45,
            [theme.tablet]: { padding: 30 },
            [theme.mobile]: { padding: 20 },
          }}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

const ToggleIcon = props => (
  <svg fill="none" viewBox="0 0 19 19" {...props}>
    <path
      stroke="#00A9DF"
      strokeLinecap="square"
      strokeWidth="2"
      d="M1.3 9.3h16.5M9.3 1.3v16.5"
    />
  </svg>
)

const activeCardStyles = {
  boxShadow: theme.cardShadow,
  borderBottom: `8px solid ${theme.midBlue}`,
}

AccordionItem.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onRequestOpen: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  className: PropTypes.string,
}

export default AccordionItem
